import { useState, useEffect } from 'react';
import { getFundManagers } from '../api';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  UserCog, Building2, Plus, X, Pencil, CheckCircle, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ROLES = [
  { value: 'COMPLIANCE_OFFICER', label: 'Compliance Officer' },
  { value: 'FUND_MANAGER', label: 'Fund Manager' },
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'ADMIN', label: 'Admin' },
];

const roleLabel = (role) => ROLES.find(r => r.value === role)?.label || role?.replace(/_/g, ' ') || '-';

const Badge = ({ children, theme }) => (
  <span style={{
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const Label = ({ children, required, theme }) => (
  <label style={{
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: 6,
  }}>
    {children} {required && <span style={{ color: '#991B1B' }}>*</span>}
  </label>
);

const getInputStyle = (theme) => ({
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${theme.border}`,
  fontSize: 13,
  color: theme.textPrimary,
  background: theme.bgInput,
  boxSizing: 'border-box',
  outline: 'none',
});

const Avatar = ({ name, theme, size = 32 }) => (
  <div style={{
    width: size,
    height: size,
    borderRadius: 8,
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size * 0.36,
    fontWeight: 600,
    flexShrink: 0,
  }}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);

const Users = () => {
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);

  const [createError, setCreateError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const emptyForm = {
    full_name: '',
    email: '',
    role: 'COMPLIANCE_OFFICER',
    fund_manager_id: '',
  };

  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const token = localStorage.getItem('token');
  const inputStyle = getInputStyle(theme);

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: '1px solid #2563EB',
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  };

  const loadUsers = () =>
    axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUsers(res.data || []));

  useEffect(() => {
    Promise.all([
      loadUsers().catch(() => setUsers([])),
      getFundManagers().then(res => setManagers(res.data || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(user => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      user.full_name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.role?.toLowerCase().includes(q) ||
      user.legal_name?.toLowerCase().includes(q)
    );
  });

  const platformUsers = users.filter(u => !u.fund_manager_id).length;
  const scopedUsers = users.length - platformUsers;
  const adminUsers = users.filter(u => u.role === 'ADMIN').length;

  const handleCreate = async () => {
    if (!createForm.full_name || !createForm.email || !createForm.role) {
      setCreateError('Full name, email and role are required.');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await axios.post(`${API_URL}/auth/register`, createForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await loadUsers();
      setShowCreate(false);
      setCreateForm(emptyForm);
      setSuccessMsg(`Account created. Setup email sent to ${createForm.email}`);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editForm.full_name || !editForm.role) {
      setEditError('Full name and role are required.');
      return;
    }

    setEditing(true);
    setEditError(null);

    try {
      await axios.patch(`${API_URL}/users/${editTarget.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await loadUsers();
      setShowEdit(false);
      setEditTarget(null);
      setSuccessMsg('User updated successfully.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update user.');
    } finally {
      setEditing(false);
    }
  };

  const openEdit = (user) => {
    setEditTarget(user);
    setEditForm({
      full_name: user.full_name || '',
      email: user.email || '',
      role: user.role || 'COMPLIANCE_OFFICER',
      fund_manager_id: user.fund_manager_id || '',
    });
    setEditError(null);
    setShowEdit(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setCreateForm(emptyForm);
    setCreateError(null);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditTarget(null);
    setEditError(null);
  };

  const ModalForm = ({ form, setForm, error, submitting, onSubmit, onClose, isEdit }) => (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: theme.bgOverlay,
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: theme.bgCard,
        borderRadius: isMobile ? '18px 18px 0 0' : 12,
        width: '100%',
        maxWidth: isMobile ? '100%' : 500,
        maxHeight: isMobile ? '92vh' : '86vh',
        overflowY: 'auto',
        boxShadow: theme.shadowLg,
        border: isMobile ? 'none' : `1px solid ${theme.border}`,
      }}>
        <div style={{
          padding: 20,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: theme.bgCard,
          zIndex: 1,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>
              {isEdit ? 'Edit user' : 'Add user'}
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>
              {isEdit ? `Editing ${editTarget?.full_name}` : 'A setup email will be sent automatically.'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: theme.bgInput,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            cursor: 'pointer',
            padding: 8,
            display: 'flex',
            color: theme.textSecondary,
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Full Name</Label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm(d => ({ ...d, full_name: e.target.value }))}
              placeholder="Jane Doe"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Email</Label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(d => ({ ...d, email: e.target.value }))}
              placeholder="jane@example.com"
              style={{ ...inputStyle, opacity: isEdit ? 0.65 : 1 }}
              disabled={isEdit}
            />
            {isEdit && (
              <p style={{ margin: '5px 0 0', fontSize: 11, color: theme.textTertiary }}>
                Email changes are handled by the user in Settings.
              </p>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label required theme={theme}>Role</Label>
            <select
              value={form.role}
              onChange={e => setForm(d => ({ ...d, role: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 18 }}>
            <Label theme={theme}>Fund Manager</Label>
            <select
              value={form.fund_manager_id}
              onChange={e => setForm(d => ({ ...d, fund_manager_id: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">None - platform-wide</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.legal_name}</option>)}
            </select>
          </div>

          {error && (
            <div style={{
              background: '#FEF2F2',
              borderRadius: 8,
              padding: '10px 13px',
              marginBottom: 14,
              fontSize: 12,
              color: '#991B1B',
              fontWeight: 600,
              border: '1px solid #FECACA',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={secondaryBtn}>
              Cancel
            </button>
            <button onClick={onSubmit} disabled={submitting} style={{
              ...primaryBtn,
              background: submitting ? '#93C5FD' : '#2563EB',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading users...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            Users
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Workspace access, roles, and fund-manager scope.
          </p>
        </div>

        <button onClick={() => { setShowCreate(true); setCreateError(null); }} style={primaryBtn}>
          <Plus size={15} /> Add User
        </button>
      </div>

      {successMsg && (
        <div style={{
          background: '#F0FDF4',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: 13,
          color: '#166534',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid #DCFCE7',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={14} />
            {successMsg}
          </div>
          <button onClick={() => setSuccessMsg(null)} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#166534',
            fontSize: 16,
            lineHeight: 1,
          }}>
            ×
          </button>
        </div>
      )}

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total users', users.length],
            ['Admins', adminUsers],
            ['Scoped users', scopedUsers],
            ['Platform-wide', platformUsers],
          ].map(([label, value], idx) => (
            <div key={label} style={{
              padding: '16px 18px',
              borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
            }}>
              <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
              <p style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>{value}</p>
            </div>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 340 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search users, roles, managers"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <span style={{ fontSize: 12, color: theme.textTertiary }}>
            Showing {filtered.length} of {users.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              User Directory
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Active platform accounts and access scopes
            </p>
          </div>
          <UserCog size={17} color={theme.textTertiary} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <UserCog size={34} color={theme.textTertiary} style={{ marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>No users found</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>Create a user or adjust your search.</p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(user => (
              <div key={user.id} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: 13,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={user.full_name} theme={theme} size={34} />
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{user.full_name}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>{user.email}</p>
                    </div>
                  </div>
                  <button onClick={() => openEdit(user)} style={{
                    background: theme.bgCard,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    padding: 7,
                    display: 'flex',
                    color: theme.textSecondary,
                  }}>
                    <Pencil size={13} />
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <Badge theme={theme}>{roleLabel(user.role)}</Badge>
                  <Badge theme={theme}>{user.legal_name || 'Platform-wide'}</Badge>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: theme.textTertiary }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['User', 'Role', 'Fund Manager', 'Created', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((user, idx) => (
                <tr key={user.id} style={{
                  borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                  transition: 'background 0.12s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={user.full_name} theme={theme} size={32} />
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{user.full_name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <Badge theme={theme}>{roleLabel(user.role)}</Badge>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {user.legal_name && <Building2 size={12} color={theme.textTertiary} />}
                      <span style={{ fontSize: 13, color: theme.textSecondary }}>{user.legal_name || 'Platform-wide'}</span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <button onClick={() => openEdit(user)} style={{
                      background: theme.bgInput,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      padding: '7px 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      color: theme.textSecondary,
                      fontSize: 12,
                      fontWeight: 600,
                    }}>
                      <Pencil size={12} />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <ModalForm
          form={createForm}
          setForm={setCreateForm}
          error={createError}
          submitting={creating}
          onSubmit={handleCreate}
          onClose={closeCreate}
          isEdit={false}
        />
      )}

      {showEdit && (
        <ModalForm
          form={editForm}
          setForm={setEditForm}
          error={editError}
          submitting={editing}
          onSubmit={handleEdit}
          onClose={closeEdit}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default Users;
